// Pipeline Prospector Design System — Blue Ridge Palette

export const designTokens = {
  colors: {
    // Brand — sunrise accent
    brand: '#C4705A',
    brandHover: '#B05A48',
    brandLight: 'rgba(196,112,90,0.15)',
    brandFaint: 'rgba(196,112,90,0.10)',
    brandSubtle: 'rgba(196,112,90,0.04)',

    background: '#1E2830',
    backgroundSecondary: '#2E3840',
    backgroundTertiary: '#3A4650',

    border: 'rgba(80,98,114,0.20)',
    borderLight: 'rgba(80,98,114,0.12)',

    text: '#E4E6E8',
    textSecondary: '#8A9CAA',
    textTertiary: '#6A7E8E',
    textInverse: '#1E2830',

    success: '#16a34a',
    successLight: 'rgba(22, 163, 74, 0.15)',
    warning: '#d97706',
    warningLight: 'rgba(217, 119, 6, 0.15)',
    error: '#dc2626',
    errorLight: 'rgba(220, 38, 38, 0.15)',

    // Acquisition score tiers
    cold: '#6A7E8E',        // 0-25: dim (far-ridge)
    warm: '#b5a06a',        // 26-50: warm sand/khaki
    hot: '#c48a5c',         // 51-75: dusty clay
    urgent: '#b85c5c',      // 76-100: muted brick

    // Pipeline group colors
    pipelineGasTransmission: '#16a34a',     // green
    pipelineLiquidsTransmission: '#2563eb', // blue
    pipelineGathering: '#e8b878',           // gold

    // Measurement tool
    measurement: '#ef4444',
    measurementLabel: '#D49880',

    // DC suitability score tiers
    dcPrime: '#1e40af',     // 80-100: navy
    dcStrong: '#3b6eb5',    // 60-79: medium blue-gray
    dcViable: '#7d9bbf',    // 40-59: light blue-gray
    dcWeak: '#6A7E8E',      // 0-39: dim (far-ridge)
    government: '#4B5563',   // government-owned: distinctly unavailable

    // Flood zone colors
    floodZone100yr: '#dc2626',
    floodZone500yr: '#f97316',

    // Highway color
    highway: '#6366f1',

    // Gas infrastructure
    gasInfraCompressor: '#e07850',
    gasInfraReceiptDelivery: '#5ca08c',
    gasInfraInterconnect: '#c49a6c',
    gasInfraStorage: '#7a8fb5',

    // Chart palette — ridge tones
    chartPrimary: '#C4705A',
    chartSecondary: '#D49880',
    chartTertiary: '#8A9CAA',
    chartQuaternary: '#6A7E8E',
    // MW / capacity
    mwPotential: '#7c3aed',
    mwAvailable: '#0891b2',
  },

  typography: {
    fontFamily: {
      display: '"DM Sans", -apple-system, sans-serif',
      mono: '"JetBrains Mono", "SF Mono", monospace',
    },
    fontSize: {
      '2xs': '9px',   // Micro labels, badge counts
      xs: '10px',      // Small labels
      'xs+': '11px',   // Table data, compact UI
      sm: '12px',      // Body small, buttons
      base: '13px',    // Body text
      md: '14px',      // Emphasized body
      lg: '18px',      // Section headers
      xl: '20px',      // Panel titles
      '2xl': '24px',   // Page title
    },
    fontWeight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  borderRadius: {
    sm: '4px',
    md: '6px',
    lg: '8px',
    full: '9999px',
  },

  shadows: {
    sm: '0 1px 2px rgba(0,0,0,0.3)',
    md: '0 4px 6px rgba(0,0,0,0.4)',
    lg: '0 10px 15px rgba(0,0,0,0.5)',
    glow: '0 0 12px rgba(196,112,90,0.15)',
  },

  layout: {
    sidebarWidth: '280px',
    detailPanelWidth: '420px',
    bottomBarHeight: '36px',
    topBarHeight: '40px',
  },
} as const;

// Acquisition score to color mapping
// URGENT requires both high score AND 10+ MW pipeline capacity
export const getScoreColor = (score: number, pipelineMw?: number | null): string => {
  if (score >= 76 && pipelineMw != null && pipelineMw >= 10) return designTokens.colors.urgent;
  if (score >= 51) return designTokens.colors.hot;
  if (score >= 26) return designTokens.colors.warm;
  return designTokens.colors.cold;
};

export const getScoreTier = (score: number, pipelineMw?: number | null): string => {
  if (score >= 76 && pipelineMw != null && pipelineMw >= 10) return 'URGENT';
  if (score >= 51) return 'HOT';
  if (score >= 26) return 'WARM';
  return 'COLD';
};

// DC suitability score helpers
export const getDCScoreColor = (score: number): string => {
  if (score >= 80) return designTokens.colors.dcPrime;
  if (score >= 60) return designTokens.colors.dcStrong;
  if (score >= 40) return designTokens.colors.dcViable;
  return designTokens.colors.dcWeak;
};

export const getDCScoreTier = (score: number): string => {
  if (score >= 80) return 'PRIME';
  if (score >= 60) return 'STRONG';
  if (score >= 40) return 'VIABLE';
  return 'WEAK';
};
